import { useLoad } from "@redotech/react-util/load";
import { CustomerInfo } from "@redotech/redo-coverage-common";
import { AttachmentStrategy } from "@redotech/redo-storefront-common/attachment-strategy";
import { WidgetAutocheck } from "@redotech/redo-storefront-widget-common/widget";
import { ShopifyCart } from "@redotech/shopify-client/ajax";
import { useMemo } from "react";
import { ShopifyExtensionClient } from "../../client/shopify-extension-client";
import { StorefrontClient } from "../../client/storefront";
import { getFeesCache } from "../../fees-cache";
import { useLocationManager } from "../../location";
import { useBuyerIdentity } from "../../utils/cart";
import { getCoverageInfoFromCoverageCalculators } from "../../utils/coverage-calculations";
import {
  determineRedoProduct,
  getCorrectlyPricedVariant,
} from "../../utils/determine-redo-product";
import { getRedoProductsCache } from "../../utils/redo-products-cache";

export function useRescuePanelCoverage({
  cart,
  storefrontClient,
  shopifyExtensionClient,
  autocheck,
}: {
  storefrontClient: StorefrontClient;
  shopifyExtensionClient: ShopifyExtensionClient;
  autocheck: WidgetAutocheck;
  cart: ShopifyCart | undefined;
}) {
  const buyerIdentity = useBuyerIdentity(storefrontClient, autocheck);

  const feesCacheLoad = useLoad(async () => {
    return await getFeesCache();
  }, []);

  const shouldAddProduct = useMemo(() => {
    return (
      autocheck?.attachmentStrategy ===
        AttachmentStrategy.CHECKOUT_BUTTON_PRODUCT &&
      autocheck.addProductOnOptInClick !== false
    );
  }, [autocheck]);

  const locationManager = useLocationManager();

  const countryLoad = useLoad(
    async (signal) => {
      // Get country from Shopify global or force IP detection if configured
      return (
        (!autocheck.forceIPAddressLocation && globalThis.Shopify?.country) ||
        (await locationManager.getCountry(signal))
      );
    },
    [autocheck.forceIPAddressLocation, locationManager],
  );

  const coverageInfoEligibilityLoad = useLoad(async () => {
    if (!cart) {
      return undefined;
    }

    // Check if using Zapiet pickup method
    const isZapietPickup = cart.attributes?.["Checkout-Method"] === "pickup";

    // Check if it's a business account
    const isBusinessAccount = !!buyerIdentity?.purchasingCompany?.company;

    // Build customer info for coverage calculation
    const customerInfo: CustomerInfo = {
      customerTags: [], // Customer tags are handled through autocheck currently
      country: countryLoad.value || "",
      isBusinessAccount,
      usingInStorePickup: isZapietPickup,
    };

    const { coverageInfo: eligibilities } =
      await getCoverageInfoFromCoverageCalculators({
        autocheck,
        customerInfo,
        cart,
        shopifyClient: storefrontClient,
        redoClient: shopifyExtensionClient,
        onTapcart: false,
      });

    return eligibilities;
  }, [
    cart,
    autocheck,
    buyerIdentity,
    storefrontClient,
    shopifyExtensionClient,
  ]);

  const _feeProduct = useMemo(() => {
    if (!cart || !coverageInfoEligibilityLoad.value) {
      return undefined;
    }

    // Determine which product and type based on eligibility
    if (coverageInfoEligibilityLoad.value.both.cartEligible) {
      return feesCacheLoad.value?.bothProduct;
    } else if (
      (autocheck.coverageEnabled ||
        autocheck.finalSaleReturns?.coverageEnabled) &&
      autocheck.packageProtection.coverageEnabled &&
      !autocheck.packageProtection.splitProducts
    ) {
      // Even though returns coverage is enabled AND package protection
      // coverage is enabled, the customer is not eligible for both products,
      // so we respect the splitProducts setting and return undefined.
      return undefined;
    } else if (coverageInfoEligibilityLoad.value.returns.cartEligible) {
      return feesCacheLoad.value?.returnProduct;
    } else if (
      coverageInfoEligibilityLoad.value.packageProtection.cartEligible
    ) {
      return feesCacheLoad.value?.packageProduct;
    }

    return undefined;
  }, [cart, coverageInfoEligibilityLoad, feesCacheLoad, autocheck]);

  const redoProductsLoad = useLoad(async () => {
    if (!feesCacheLoad.value) {
      return undefined;
    }

    return await getRedoProductsCache({
      autocheck,
      fees: feesCacheLoad.value,
      storefrontClient,
    });
  }, [autocheck, feesCacheLoad.value, storefrontClient]);

  const correctVariant = useMemo(() => {
    if (
      !redoProductsLoad.value ||
      !feesCacheLoad.value ||
      !coverageInfoEligibilityLoad.value ||
      !shouldAddProduct ||
      !_feeProduct?.type ||
      !redoProductsLoad.value?.mainShopifyProduct
    ) {
      return undefined;
    }

    const selectedProduct = determineRedoProduct({
      mainRedoProduct: redoProductsLoad.value.mainShopifyProduct,
      returnRedoProduct: redoProductsLoad.value.returnShopifyProduct,
      packageRedoProduct: redoProductsLoad.value.packageShopifyProduct,
      bothRedoProduct: redoProductsLoad.value.bothShopifyProduct,
      fees: feesCacheLoad.value,
      autocheck,
      coverageInfo: coverageInfoEligibilityLoad.value,
      toggleStates: { returnToggleState: true, protectionToggleState: true }, // Default to both enabled for rescue panel
    });

    return getCorrectlyPricedVariant({
      product: selectedProduct,
      productType: _feeProduct?.type,
      coverageInfo: coverageInfoEligibilityLoad.value,
      country: countryLoad.value || "",
    });
  }, [
    _feeProduct,
    redoProductsLoad,
    feesCacheLoad,
    coverageInfoEligibilityLoad,
    countryLoad,
    shouldAddProduct,
    autocheck,
  ]);

  return { correctVariant, coverageInfoEligibilityLoad };
}
